import { useState } from 'react';
import type { DateTime } from 'luxon';
import { OptionButtonGroup } from '@/components/ui/option-button-group';
import { useIsMobile } from '@/hooks/use-mobile';
import { useI18nContext } from '@/i18n/i18n-react';
import {
  EVENING_HOUR,
  MORNING_HOUR,
  getPrimaryAction,
  isTodayEveningAvailable,
  type ScheduleChoice,
} from '@/lib/campaign-schedule';
import { mobileLayer } from '@/lib/mobile-layer';
import { timezoneLabel } from '@/lib/timezone-label';
import { cn, getLuxonLocale } from '@/lib/utils';
import {
  CustomScheduleSheet,
  type CustomSchedule,
} from './custom-schedule-sheet';

type ChipKind = ScheduleChoice['kind'];

const hourLabel = (hour: number) => `${String(hour).padStart(2, '0')}:00`;

export interface ScheduleFieldProps {
  value: ScheduleChoice | null;
  timezone: string;
  now: DateTime;
  onChange(value: ScheduleChoice): void;
  onTimezoneChange(timezone: string): void;
}

/**
 * "Quando enviar" (PUSH-05 AC 3–4): Agora · Hoje, 19:00 (only while
 * `isTodayEveningAvailable`) · Amanhã, 10:00 · Outra data (labelled
 * "7 nov, 10:00" once applied). Pills below 768px, `OptionButtonGroup` from
 * 768px up. Always names the timezone in plain words ("Horário de
 * Brasília"), stressed when it is not the browser's (PUSH-12).
 */
// SPEC_DEVIATION: owns the "Outra data" sheet and reports the timezone via
// `onTimezoneChange` instead of design A8's `onOpenCustom`.
// Reason: keeps the sheet's open state out of the presenter's props.
export function ScheduleField({
  value,
  timezone,
  now,
  onChange,
  onTimezoneChange,
}: ScheduleFieldProps) {
  const { LL, locale } = useI18nContext();
  const isMobile = useIsMobile();
  const [customOpen, setCustomOpen] = useState(false);

  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const action = getPrimaryAction(
    value?.kind === 'custom' ? value : null,
    now,
    timezone,
    locale
  );
  const customLabel =
    action.kind === 'schedule'
      ? LL.campaignComposer.schedule.customValue({
          date: action.dateLabel,
          time: action.time,
        })
      : LL.campaignComposer.schedule.custom();

  const chips: { value: ChipKind; label: string }[] = [
    { value: 'now', label: LL.campaignComposer.schedule.now() },
    ...(isTodayEveningAvailable(now, timezone)
      ? [
          {
            value: 'todayEvening' as const,
            label: LL.campaignComposer.schedule.todayAt({
              time: hourLabel(EVENING_HOUR),
            }),
          },
        ]
      : []),
    {
      value: 'tomorrowMorning',
      label: LL.campaignComposer.schedule.tomorrowAt({
        time: hourLabel(MORNING_HOUR),
      }),
    },
    { value: 'custom', label: customLabel },
  ];

  const choose = (kind: ChipKind) => {
    if (kind === 'custom') {
      setCustomOpen(true);
      return;
    }
    onChange({ kind });
  };

  const apply = ({ date, time, timezone: tz }: CustomSchedule) => {
    if (tz !== timezone) onTimezoneChange(tz);
    onChange({ kind: 'custom', date, time });
    setCustomOpen(false);
  };

  const groupLabel = LL.campaignComposer.schedule.label();

  return (
    <div className="space-y-2">
      {isMobile ? (
        <div
          role="group"
          aria-label={groupLabel}
          className="flex flex-wrap gap-2"
        >
          {chips.map((chip) => {
            const pressed = value?.kind === chip.value;
            return (
              <button
                key={chip.value}
                type="button"
                aria-pressed={pressed}
                onClick={() => choose(chip.value)}
                className={cn(
                  'inline-flex min-h-touch items-center whitespace-nowrap border px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  mobileLayer.chip,
                  pressed
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-input bg-background text-foreground'
                )}
              >
                {chip.label}
              </button>
            );
          })}
        </div>
      ) : (
        <OptionButtonGroup<ChipKind | 'none'>
          value={value?.kind ?? 'none'}
          options={chips}
          onChange={(kind) => {
            if (kind !== 'none') choose(kind);
          }}
          ariaLabel={groupLabel}
          className="flex-wrap"
        />
      )}
      {/* Always said, in plain words ("Horário de Brasília"): the chips'
          times are in this zone. */}
      <p
        className={cn(
          'text-sm text-muted-foreground',
          timezone !== browserTimezone && 'font-medium text-foreground'
        )}
      >
        {LL.campaignComposer.schedule.timezoneNote({
          timezone: timezoneLabel(timezone, getLuxonLocale(locale)),
        })}
      </p>
      <CustomScheduleSheet
        open={customOpen}
        onOpenChange={setCustomOpen}
        initial={
          value?.kind === 'custom'
            ? { date: value.date, time: value.time }
            : null
        }
        timezone={timezone}
        now={now}
        onApply={apply}
      />
    </div>
  );
}
